import React, { useMemo } from 'react';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import { PopoverOrigin } from '@mui/material/Popover';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export const SECTION_HEADER_MENU_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};

export const SECTION_HEADER_MENU_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

/** 3-column sidebar kebab: open to the right of the trigger (not downward). */
export const SECTION_HEADER_SIDEBAR_KEBAB_MENU_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

export const SECTION_HEADER_SIDEBAR_KEBAB_MENU_TRANSFORM_ORIGIN: PopoverOrigin =
  {
    vertical: 'top',
    horizontal: 'left',
  };

/** Row ids for the section header tab overflow menu (EditableHeadersTabsSectionMenu). */
export const SECTION_HEADER_TABS_MENU_ROW_ID = {
  reorder: 'reorder',
  edit: 'edit',
  copy: 'copy',
  hide: 'hide',
  remove: 'remove',
} as const;

export type SectionHeaderTabsMenuRowId =
  (typeof SECTION_HEADER_TABS_MENU_ROW_ID)[keyof typeof SECTION_HEADER_TABS_MENU_ROW_ID];

/** Row keys for the section header kebab menu (EditableSectionHeaderKebabMenu). */
export const SECTION_HEADER_KEBAB_MENU_ROW_KEY = {
  edit: 'edit',
  hide: 'hide',
  remove: 'remove',
} as const;

export type SectionHeaderKebabMenuRowKey =
  (typeof SECTION_HEADER_KEBAB_MENU_ROW_KEY)[keyof typeof SECTION_HEADER_KEBAB_MENU_ROW_KEY];

export const getSectionHeaderVisibilityMenuRow = (
  hiddenFromVisitors: boolean
): { icon: React.ReactElement; messageKey: string } => ({
  icon: hiddenFromVisitors ? (
    <VisibilityOff fontSize="small" />
  ) : (
    <Visibility fontSize="small" />
  ),
  messageKey: hiddenFromVisitors
    ? 'rooms.editor.section_header.tabs_menu.unhide'
    : 'rooms.editor.section_header.tabs_menu.hide',
});

export const isSectionHeaderDeepLinkCopyDisabled = (
  copySectionDeepLinkDisabled?: boolean,
  hiddenFromVisitors?: boolean
): boolean => Boolean(copySectionDeepLinkDisabled || hiddenFromVisitors);

/** Lingui key: copy section/asset deep link disabled until room is published. */
export const SECTION_HEADER_DEEP_LINK_COPY_DISABLED_I18N_KEY =
  'rooms.editor.deep_link_copy_requires_save';

/** Lingui key: copy section deep link disabled while section is hidden. */
export const SECTION_HEADER_DEEP_LINK_COPY_HIDDEN_I18N_KEY =
  'rooms.editor.section_header.copy_link_requires_unhide';

/** Lingui key: copy content deep link disabled while section is hidden. */
export const CONTENT_DEEP_LINK_COPY_HIDDEN_I18N_KEY =
  'rooms.editor.content.copy_link_requires_visible';

export const getDeepLinkCopyDisabledTooltip = (
  t: (id: string) => string,
  baseCopySectionDeepLinkDisabled: boolean | undefined,
  hiddenFromVisitors: boolean | undefined,
  hiddenTooltipKey: string
): string | undefined => {
  if (
    !isSectionHeaderDeepLinkCopyDisabled(
      baseCopySectionDeepLinkDisabled,
      hiddenFromVisitors
    )
  ) {
    return undefined;
  }

  if (hiddenFromVisitors) {
    return t(hiddenTooltipKey);
  }

  return t(SECTION_HEADER_DEEP_LINK_COPY_DISABLED_I18N_KEY);
};

export const useDeepLinkCopyDisabledProps = (
  baseCopySectionDeepLinkDisabled: boolean | undefined,
  hiddenFromVisitors: boolean | undefined,
  hiddenTooltipKey: string,
  t: (id: string) => string
) =>
  useMemo(
    () => ({
      copySectionDeepLinkDisabled: isSectionHeaderDeepLinkCopyDisabled(
        baseCopySectionDeepLinkDisabled,
        hiddenFromVisitors
      ),
      copySectionDeepLinkDisabledTooltip: getDeepLinkCopyDisabledTooltip(
        t,
        baseCopySectionDeepLinkDisabled,
        hiddenFromVisitors,
        hiddenTooltipKey
      ),
    }),
    [baseCopySectionDeepLinkDisabled, hiddenFromVisitors, hiddenTooltipKey, t]
  );

export const useEditableStructureDeepLinkCopyProps = (
  baseCopySectionDeepLinkDisabled: boolean | undefined,
  sectionHiddenFromVisitors: boolean | undefined,
  t: (id: string) => string
) =>
  useMemo(
    () => ({
      copySectionDeepLinkDisabled: isSectionHeaderDeepLinkCopyDisabled(
        baseCopySectionDeepLinkDisabled,
        sectionHiddenFromVisitors
      ),
      copySectionDeepLinkDisabledTooltip: getDeepLinkCopyDisabledTooltip(
        t,
        baseCopySectionDeepLinkDisabled,
        sectionHiddenFromVisitors,
        SECTION_HEADER_DEEP_LINK_COPY_HIDDEN_I18N_KEY
      ),
      contentCopyDeepLinkDisabledTooltip: getDeepLinkCopyDisabledTooltip(
        t,
        baseCopySectionDeepLinkDisabled,
        sectionHiddenFromVisitors,
        CONTENT_DEEP_LINK_COPY_HIDDEN_I18N_KEY
      ),
    }),
    [baseCopySectionDeepLinkDisabled, sectionHiddenFromVisitors, t]
  );

type SectionHeaderMenuItemProps = {
  destructive?: boolean;
  icon: React.ReactNode;
  label: React.ReactNode;
  labelVariant?: TypographyProps['variant'];
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
};

export const useSectionHeaderDeepLinkCopyMenuItemProps = (
  baseCopySectionDeepLinkDisabled: boolean | undefined,
  hiddenFromVisitors: boolean | undefined,
  t: (id: string) => string
): Pick<SectionHeaderMenuItemProps, 'disabled' | 'disabledTooltip'> => {
  const { copySectionDeepLinkDisabledTooltip } = useDeepLinkCopyDisabledProps(
    baseCopySectionDeepLinkDisabled,
    hiddenFromVisitors,
    SECTION_HEADER_DEEP_LINK_COPY_HIDDEN_I18N_KEY,
    t
  );

  return useMemo(
    () =>
      copySectionDeepLinkDisabledTooltip
        ? {
            disabled: true,
            disabledTooltip: copySectionDeepLinkDisabledTooltip,
          }
        : {},
    [copySectionDeepLinkDisabledTooltip]
  );
};

export const SectionHeaderMenuItem: React.FC<SectionHeaderMenuItemProps> = ({
  destructive,
  icon,
  label,
  labelVariant,
  onClick,
  disabled,
  disabledTooltip,
}) => {
  const menuItem = (
    <MenuItem
      dense
      disabled={disabled}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        ...(destructive ? { color: 'error.main' } : {}),
      }}
    >
      <ListItemIcon sx={destructive ? { color: 'inherit' } : undefined}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={
          labelVariant ? (
            <Typography
              color={destructive ? 'inherit' : undefined}
              sx={{ textTransform: 'none' }}
              variant={labelVariant}
            >
              {label}
            </Typography>
          ) : (
            label
          )
        }
      />
    </MenuItem>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} placement="left" enterTouchDelay={0}>
        <span style={{ display: 'block' }}>{menuItem}</span>
      </Tooltip>
    );
  }

  return menuItem;
};
